import { useState, useEffect } from 'react';

const AGE_VERIFICATION_KEY = 'alpha_v8_age_verified';
const VERIFICATION_EXPIRY_DAYS = 1;

interface VerificationResult {
  success: boolean;
  targetRoute?: string;
  error?: string;
}

export const useAgeVerification = () => {
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkVerificationStatus = () => {
    try {
      console.log('=== AGE VERIFICATION: Checking status ===');
      console.log('Current domain:', window.location.hostname);
      console.log('Current pathname:', window.location.pathname);
      console.log('User agent:', navigator.userAgent);
      console.log('Is incognito/private mode likely:', isLikelyIncognito());
      
      let stored = null;
      let storageMethod = 'none';
      
      // Check memory storage first (most reliable in incognito)
      if ((window as any).alpha_v8_age_verified) {
        stored = (window as any).alpha_v8_age_verified;
        storageMethod = 'memory';
        console.log('Found verification in memory storage');
      }
      
      // Try localStorage if memory is empty
      if (!stored) {
        try {
          if (typeof Storage !== 'undefined' && localStorage) {
            stored = localStorage.getItem(AGE_VERIFICATION_KEY);
            if (stored) {
              storageMethod = 'localStorage';
              console.log('Found verification in localStorage');
            }
          }
        } catch (localStorageError) {
          console.warn('localStorage unavailable or blocked:', localStorageError);
        }
      }
      
      // Try sessionStorage as fallback
      if (!stored) {
        try {
          if (typeof Storage !== 'undefined' && sessionStorage) {
            stored = sessionStorage.getItem(AGE_VERIFICATION_KEY);
            if (stored) {
              storageMethod = 'sessionStorage';
              console.log('Found verification in sessionStorage');
            }
          }
        } catch (sessionStorageError) {
          console.warn('sessionStorage unavailable or blocked:', sessionStorageError);
        }
      }
      
      console.log('Storage method used:', storageMethod);
      
      if (stored) {
        try {
          const { verified, timestamp } = JSON.parse(stored);
          const now = new Date().getTime();
          const expiryTime = timestamp + (VERIFICATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
          
          console.log('Verification data found:', { 
            verified, 
            timestamp, 
            now, 
            expiryTime, 
            expired: now >= expiryTime,
            storageMethod 
          });
          
          if (now < expiryTime && verified) {
            console.log('✅ User is verified and not expired');
            setIsVerified(true);
          } else {
            console.log('⏰ Verification expired or invalid, clearing storage');
            clearVerificationFromStorage();
            setIsVerified(false);
          }
        } catch (parseError) {
          console.error('Failed to parse verification data:', parseError);
          clearVerificationFromStorage();
          setIsVerified(false);
        }
      } else {
        console.log('❌ No verification found in any storage method');
        setIsVerified(false);
      }
    } catch (error) {
      console.error('❌ Error checking age verification:', error);
      setError(`Verification check failed: ${error}`);
      setIsVerified(false);
    } finally {
      setLoading(false);
    }
  };

  const isLikelyIncognito = () => {
    // Simple heuristics to detect incognito/private mode
    try {
      // Check if storage quota is very low (common in incognito)
      if (navigator.storage && navigator.storage.estimate) {
        navigator.storage.estimate().then(estimate => {
          console.log('Storage quota:', estimate.quota);
          return estimate.quota && estimate.quota < 120000000; // Less than ~120MB suggests incognito
        });
      }
      
      // Check localStorage behavior
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return false;
      } catch {
        return true;
      }
    } catch {
      return true;
    }
  };

  const clearVerificationFromStorage = () => {
    // Clear from all possible storage locations
    try {
      delete (window as any).alpha_v8_age_verified;
      console.log('Cleared verification from memory storage');
    } catch (e) {
      console.warn('Failed to clear memory storage:', e);
    }
    
    try {
      localStorage.removeItem(AGE_VERIFICATION_KEY);
      console.log('Cleared verification from localStorage');
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
    
    try {
      sessionStorage.removeItem(AGE_VERIFICATION_KEY);
      console.log('Cleared verification from sessionStorage');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
  };

  const verifyAge = async (): Promise<VerificationResult> => {
    console.log('=== AGE VERIFICATION: Starting verification ===');
    console.log('Browser detected:', getBrowserInfo());
    console.log('Likely incognito mode:', isLikelyIncognito());
    
    setError(null);
    
    const verificationData = {
      verified: true,
      timestamp: new Date().getTime()
    };
    
    const dataString = JSON.stringify(verificationData);
    console.log('Storing verification data:', verificationData);
    
    // Enhanced storage strategy - prioritize memory storage for incognito
    let storageSuccess = false;
    let storageMethod = 'none';
    let storageWarning = null;
    
    // Try memory storage first (most reliable in incognito)
    try {
      (window as any).alpha_v8_age_verified = dataString;
      storageSuccess = true;
      storageMethod = 'memory';
      console.log('✅ Verification stored in memory');
      
      if (isLikelyIncognito()) {
        storageWarning = 'Running in private/incognito mode - verification will not persist if you refresh the page';
      }
    } catch (memoryError) {
      console.error('Memory storage failed:', memoryError);
    }
    
    // Try localStorage as backup (if not in incognito)
    if (storageSuccess && !isLikelyIncognito()) {
      try {
        if (typeof Storage !== 'undefined' && localStorage) {
          localStorage.setItem(AGE_VERIFICATION_KEY, dataString);
          // Verify it was actually stored
          const testRetrieve = localStorage.getItem(AGE_VERIFICATION_KEY);
          if (testRetrieve === dataString) {
            storageMethod = 'localStorage+memory';
            console.log('✅ Verification also stored in localStorage');
          }
        }
      } catch (localStorageError) {
        console.warn('localStorage failed (expected in incognito):', localStorageError);
      }
    }
    
    // Try sessionStorage as additional backup
    if (storageSuccess) {
      try {
        if (typeof Storage !== 'undefined' && sessionStorage) {
          sessionStorage.setItem(AGE_VERIFICATION_KEY, dataString);
          const testRetrieve = sessionStorage.getItem(AGE_VERIFICATION_KEY);
          if (testRetrieve === dataString) {
            storageMethod = storageMethod.includes('localStorage') ? 'localStorage+session+memory' : 'session+memory';
            console.log('✅ Verification also stored in sessionStorage');
          }
        }
      } catch (sessionStorageError) {
        console.warn('sessionStorage failed:', sessionStorageError);
      }
    }
    
    if (storageSuccess) {
      setIsVerified(true);
      
      if (storageWarning) {
        setError(storageWarning);
      }
      
      console.log('✅ Age verification completed successfully');
      console.log('Storage method used:', storageMethod);
      console.log('Target route: /auth');
      
      return { 
        success: true, 
        targetRoute: '/auth'
      };
    } else {
      console.error('❌ All storage methods failed');
      setError('Unable to store verification data. Please try refreshing the page.');
      return {
        success: false,
        error: 'Storage failed - please try refreshing the page'
      };
    }
  };

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Brave')) return 'Brave';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  };

  const clearVerification = () => {
    console.log('=== AGE VERIFICATION: Manually clearing verification ===');
    clearVerificationFromStorage();
    setIsVerified(false);
    setError(null);
  };

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  return {
    isVerified,
    loading,
    error,
    verifyAge,
    clearVerification
  };
};
